import React, { useState } from 'react';
import { TopNav } from '../TopNav';
import { MainNav, MainNavItemData } from '../MainNav';
import { Sidebar, SidebarProps, MenuSectionData } from '../Sidebar';
import { Footer, FooterLinkSection, SocialLink } from '../Footer';
import { Breadcrumbs, BreadcrumbItem } from '../../molecules/Breadcrumbs';
import { LayoutContainer } from '../../atoms/LayoutContainer';
import { Flex } from '../../atoms/Flex';
import './Page.css';

export interface PageProps {
  /**
   * Whether the user is logged in
   */
  isLoggedIn?: boolean;
  /**
   * Whether to show the search bar in TopNav
   */
  showSearch?: boolean;
  /**
   * The www domain for URLs (e.g., 'www.heartinternet.uk')
   */
  wwwDomain?: string;

  /**
   * MainNav items data
   */
  mainNavItems?: MainNavItemData[];

  /**
   * Breadcrumb items
   */
  breadcrumbs?: BreadcrumbItem[];

  /**
   * Sidebar menu sections
   */
  sidebarSections?: MenuSectionData[];
  /**
   * Whether to show the cart button in sidebar
   */
  showCart?: boolean;
  /**
   * Whether to show the resume purchase box
   */
  showResumePurchase?: boolean;
  /**
   * Resume purchase box data
   */
  resumePurchaseData?: SidebarProps['resumePurchaseData'];

  /**
   * Footer link sections
   */
  footerSections?: FooterLinkSection[];
  /**
   * Footer social links
   */
  footerSocialLinks?: SocialLink[];
  /**
   * Footer blog link
   */
  footerBlogLink?: { label: string; href: string };

  /**
   * Hide the sidebar and layout container, rendering children full-width.
   * Use for pre-login pages (e.g. RegisterPage) that manage their own layout.
   */
  hideSidebar?: boolean;

  /**
   * Page content (the page-specific component)
   */
  children: React.ReactNode;

  /**
   * Custom CSS classes
   */
  className?: string;
}

export const Page: React.FC<PageProps> = ({
  isLoggedIn = true,
  showSearch = true,
  wwwDomain = 'www.heartinternet.uk',
  mainNavItems = [],
  breadcrumbs,
  sidebarSections = [],
  showCart = true,
  showResumePurchase = false,
  resumePurchaseData,
  footerSections = [],
  footerSocialLinks = [],
  footerBlogLink,
  hideSidebar = false,
  children,
  className = '',
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const pageClasses = `hi-ui hi-ui-page ${className}`.trim();

  const handleAccountClick = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleMenuToggle = (isOpen: boolean) => {
    // When hamburger menu opens, close the sidebar
    if (isOpen) {
      setSidebarOpen(false);
    }
  };

  return (
    <div className={pageClasses}>
      {/* Top Navigation */}
      <TopNav isLoggedIn={isLoggedIn} showSearch={showSearch} wwwDomain={wwwDomain} />

      {/* Main Navigation */}
      <MainNav
        items={mainNavItems}
        isLoggedIn={isLoggedIn}
        wwwDomain={wwwDomain}
        onAccountClick={handleAccountClick}
        onMenuToggle={handleMenuToggle}
        sidebarOpen={sidebarOpen}
      />

      {/* Page Content Area */}
      {hideSidebar ? (
        <div className="hi-ui-page__container hi-ui-page__container--full-width">{children}</div>
      ) : (
        <LayoutContainer className="hi-ui-page__container">
          <Flex className="hi-ui-page__layout" gap="lg">
            {/* Main Content */}
            <Flex as="main" direction="column" className="hi-ui-page__content" flex="1">
              {breadcrumbs && breadcrumbs.length > 0 && (
                <Breadcrumbs items={breadcrumbs} className="hi-ui-page__breadcrumbs" />
              )}
              {children}
            </Flex>

            {/* Sidebar */}
            <Sidebar
              sections={sidebarSections}
              showCart={showCart}
              showResumePurchase={showResumePurchase}
              resumePurchaseData={resumePurchaseData}
              isOpen={sidebarOpen}
            />
          </Flex>
        </LayoutContainer>
      )}

      {/* Footer */}
      <Footer
        sections={footerSections}
        socialLinks={footerSocialLinks}
        blogLink={footerBlogLink}
        wwwDomain={wwwDomain}
      />
    </div>
  );
};
